import { useCallback, useEffect, useRef, useState } from 'react'
import { buildBackendUrl } from '../utils/backendUrl'
import {
  DebugSettings,
  DebugSettingKey,
  DEFAULT_DEBUG_SETTINGS,
  DEBUG_SETTINGS_EVENT,
  DebugSettingsEventDetail,
  normalizeDebugSettings,
  persistDebugSettingsToStorage,
  DEBUG_SETTINGS_STORAGE_KEY,
  readDebugSettingsFromStorage,
} from '../utils/debugSettingsShared'

export type { DebugSettingKey } from '../utils/debugSettingsShared'

const DEFAULT_SETTINGS: DebugSettings = { ...DEFAULT_DEBUG_SETTINGS }

const BROADCAST_CHANNEL = 'ogb-debug-settings'
const getSettingsEndpoint = () => `${buildBackendUrl('/api/debug/settings')}`
const getAdminSettingsEndpoint = () => `${buildBackendUrl('/admin/debug/settings')}`

interface ServerSettingsSnapshot {
  settings: DebugSettings
  lastUpdatedAt: string | null
}

const getAdminAuthHeader = (): string | null => {
  if (typeof window === 'undefined') {
    return null
  }
  return window.localStorage.getItem('admin_auth')
}

const fetchServerSettings = async (): Promise<ServerSettingsSnapshot | null> => {
  try {
    const response = await fetch(`${getSettingsEndpoint()}?v=${Date.now()}`, {
      headers: {
        'Cache-Control': 'no-cache',
      },
    })

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`)
    }

    const payload = await response.json()
    const incoming = payload?.settings ?? payload
    if (incoming) {
      return {
        settings: {
        ...DEFAULT_SETTINGS,
        ...incoming,
        },
        lastUpdatedAt: payload?.lastUpdatedAt ?? null,
      }
    }
  } catch (error) {
    console.warn('AGC: Failed to fetch debug settings from backend, using cached copy.', error)
  }

  return null
}

export function useDebugSettings() {
  const [debugSettings, setDebugSettings] = useState<DebugSettings>(() => readDebugSettingsFromStorage())
  const [isLoading, setIsLoading] = useState(true)
  const [lastUpdatedAt, setLastUpdatedAt] = useState<string | null>(null)
  const broadcastRef = useRef<BroadcastChannel | null>(null)
  const settingsRef = useRef<DebugSettings>(debugSettings)
  const lastUpdatedAtRef = useRef<string | null>(null)

  useEffect(() => {
    settingsRef.current = debugSettings
  }, [debugSettings])

  useEffect(() => {
    lastUpdatedAtRef.current = lastUpdatedAt
  }, [lastUpdatedAt])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const handleStorage = (event: StorageEvent) => {
      if (event.key === DEBUG_SETTINGS_STORAGE_KEY) {
        setDebugSettings(readDebugSettingsFromStorage())
      }
    }

    window.addEventListener('storage', handleStorage)

    if ('BroadcastChannel' in window) {
      broadcastRef.current = new BroadcastChannel(BROADCAST_CHANNEL)
      broadcastRef.current.onmessage = (event) => {
        const detail = (event?.data?.detail ?? null) as DebugSettingsEventDetail | null
        if (detail?.settings) {
          setDebugSettings(normalizeDebugSettings(detail.settings))
          if (detail.lastUpdatedAt) {
            setLastUpdatedAt(detail.lastUpdatedAt)
          }
        } else {
          setDebugSettings(readDebugSettingsFromStorage())
        }
      }
    }

    const handleCustomEvent = (event: Event) => {
      if (event.type === DEBUG_SETTINGS_EVENT) {
        const customEvent = event as CustomEvent<DebugSettingsEventDetail>
        const detail = customEvent.detail
        if (detail?.settings) {
          setDebugSettings(normalizeDebugSettings(detail.settings))
          if (detail.lastUpdatedAt) {
            setLastUpdatedAt(detail.lastUpdatedAt)
          }
        } else {
          setDebugSettings(readDebugSettingsFromStorage())
        }
      }
    }

    window.addEventListener(DEBUG_SETTINGS_EVENT, handleCustomEvent as EventListener)

    return () => {
      window.removeEventListener('storage', handleStorage)
      window.removeEventListener(DEBUG_SETTINGS_EVENT, handleCustomEvent as EventListener)
      broadcastRef.current?.close()
      broadcastRef.current = null
    }
  }, [])

  const emitDebugSettingsEvent = useCallback(
    (settings: DebugSettings, lastUpdatedAtValue?: string | null) => {
      if (typeof window === 'undefined') {
        return
      }
      const detail: DebugSettingsEventDetail = {
        settings,
        lastUpdatedAt: lastUpdatedAtValue ?? null,
      }
      try {
        broadcastRef.current?.postMessage({ type: DEBUG_SETTINGS_EVENT, detail })
      } catch (error) {
        console.warn('AGC: Failed to broadcast debug settings update via channel', error)
      }

      try {
        window.dispatchEvent(new CustomEvent<DebugSettingsEventDetail>(DEBUG_SETTINGS_EVENT, { detail }))
      } catch (error) {
        console.warn('AGC: Failed to dispatch debug settings event', error)
      }
    },
    [],
  )

  const applySettings = useCallback(
    (
      snapshot: DebugSettings,
      meta?: { lastUpdatedAt?: string | null },
      options?: { persist?: boolean; broadcast?: boolean },
    ) => {
      const normalized = normalizeDebugSettings(snapshot)
      setDebugSettings(normalized)
      if (meta?.lastUpdatedAt) {
        setLastUpdatedAt(meta.lastUpdatedAt)
      }
      if (options?.persist !== false) {
        persistDebugSettingsToStorage(normalized)
      }
      if (options?.broadcast !== false) {
        emitDebugSettingsEvent(normalized, meta?.lastUpdatedAt ?? null)
      }
    },
    [emitDebugSettingsEvent],
  )

  const syncFromServer = useCallback(async () => {
    setIsLoading(true)
    const serverSettings = await fetchServerSettings()
    if (serverSettings) {
      applySettings(serverSettings.settings, { lastUpdatedAt: serverSettings.lastUpdatedAt })
    }
    setIsLoading(false)
  }, [applySettings])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const serverSettings = await fetchServerSettings()
      if (serverSettings && !cancelled) {
        applySettings(serverSettings.settings, { lastUpdatedAt: serverSettings.lastUpdatedAt })
      }
      if (!cancelled) {
        setIsLoading(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [applySettings])

useEffect(() => {
  if (typeof window === 'undefined') {
    return
  }

  const intervalId = window.setInterval(async () => {
    const snapshot = await fetchServerSettings()
    if (!snapshot) {
      return
    }

    const serializedIncoming = JSON.stringify(snapshot.settings)
    const serializedCurrent = JSON.stringify(settingsRef.current)
    const incomingUpdatedAt = snapshot.lastUpdatedAt ?? null

    if (serializedIncoming !== serializedCurrent || incomingUpdatedAt !== lastUpdatedAtRef.current) {
      applySettings(snapshot.settings, { lastUpdatedAt: incomingUpdatedAt })
    }
  }, 60000)

  return () => {
    window.clearInterval(intervalId)
  }
}, [applySettings])

  const pushSettingsToServer = useCallback(
    async (next: DebugSettings) => {
      const adminAuth = getAdminAuthHeader()
      if (!adminAuth) {
        console.log('⚠️ AGC: No admin auth found, skipping server push')
        return
      }

      console.log('📤 AGC: Pushing settings to server:', next)
      try {
        const response = await fetch(getAdminSettingsEndpoint(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: adminAuth,
          },
          body: JSON.stringify({ settings: next }),
        })

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`)
        }

        const payload = await response.json()
        console.log('✅ AGC: Server responded with:', payload)
        const snapshot = payload?.settings ?? payload
        if (snapshot) {
          applySettings(snapshot, { lastUpdatedAt: payload?.lastUpdatedAt ?? null })
        }
      } catch (error) {
        console.error('❌ AGC: Failed to push debug settings to backend. Reverting to server state.', error)
        syncFromServer()
      }
    },
    [applySettings, syncFromServer],
  )

  const updateSettings = useCallback(
    (updates: Partial<DebugSettings>) => {
      console.log('🔧 AGC: updateSettings called with:', updates)
      setDebugSettings((prev) => {
        const next = normalizeDebugSettings({ ...prev, ...updates })
        console.log('🔧 AGC: Updating debug settings:', { prev, next })
        persistDebugSettingsToStorage(next)
        emitDebugSettingsEvent(next, lastUpdatedAtRef.current)
        pushSettingsToServer(next)
        return next
      })
    },
    [emitDebugSettingsEvent, pushSettingsToServer],
  )

  const toggleSetting = useCallback(
    (key: keyof DebugSettings) => {
      setDebugSettings((prev) => {
        const next = { ...prev, [key]: !prev[key] }
        persistDebugSettingsToStorage(next)
        emitDebugSettingsEvent(next, lastUpdatedAtRef.current)
        pushSettingsToServer(next)
        return next
      })
    },
    [emitDebugSettingsEvent, pushSettingsToServer],
  )

  const updateSetting = useCallback(
    (key: keyof DebugSettings, value: boolean) => {
      updateSettings({ [key]: value })
    },
    [updateSettings],
  )

  const resetSettings = useCallback(() => {
    applySettings(DEFAULT_SETTINGS)
    pushSettingsToServer(DEFAULT_SETTINGS)
  }, [applySettings, pushSettingsToServer])

  return {
    debugSettings,
    lastUpdatedAt,
    updateSettings,
    toggleSetting,
    isLoading,
    refreshSettings: syncFromServer,
    // AGC: Legacy return fields (for older components)
    settings: debugSettings,
    updateSetting,
    resetSettings,
  }
}

export function isDebugFeatureEnabled(feature: keyof DebugSettings): boolean {
  const settings = readDebugSettingsFromStorage()
  return settings[feature]
}